'use client';

import type { StackedField } from '../types';

interface StackedCellRendererProps {
  rowData: any;
  stackedFields: StackedField[];
}

// Size classes mapping
const sizeClasses: Record<string, string> = {
  xs: 'text-[10px] leading-tight',
  sm: 'text-xs leading-tight',
  md: 'text-sm leading-normal',
  lg: 'text-base leading-normal',
};

// Font weight classes mapping
const fontWeightClasses: Record<string, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

// Format value based on format type
const formatValue = (value: any, format?: 'date' | 'currency' | 'number'): string => {
  if (value === null || value === undefined) return '';

  switch (format) {
    case 'date':
      try {
        const date = new Date(value);
        return date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      } catch {
        return String(value);
      }
    case 'currency':
      try {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 0,
        }).format(Number(value));
      } catch {
        return String(value);
      }
    case 'number':
      try {
        return new Intl.NumberFormat('en-IN').format(Number(value));
      } catch {
        return String(value);
      }
    default:
      return String(value);
  }
};

// Get nested field value using dot notation
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

// Evaluate condition expression
const evaluateCondition = (condition: string, row: any): boolean => {
  try {
    // Create a safe evaluation context
    const func = new Function('row', `return ${condition}`);
    return func(row);
  } catch (error) {
    console.warn('Failed to evaluate condition:', condition, error);
    return true; // Default to showing the field if condition fails
  }
};

export const StackedCellRenderer: React.FC<StackedCellRendererProps> = ({
  rowData,
  stackedFields,
}) => {
  return (
    <div className="flex flex-col gap-0.5 py-1">
      {stackedFields.map((field, index) => {
        // Check condition if provided
        if (field.condition && !evaluateCondition(field.condition, rowData)) {
          return null;
        }

        // Get the value
        const rawValue = getNestedValue(rowData, field.field);
        const value = formatValue(rawValue, field.format);

        // Handle empty values
        if (!value && !field.fallback) {
          return null;
        }

        const displayValue = value || field.fallback || '';

        // Build classes
        const sizeClass = sizeClasses[field.size || 'sm'];
        const weightClass = fontWeightClasses[field.fontWeight || 'normal'];
        const colorClass = field.color || 'text-gray-700';

        return (
          <div
            key={`${field.field}-${index}`}
            className={`${sizeClass} ${weightClass} ${colorClass} truncate`}
          >
            {field.label && (
              <span className="text-gray-500 font-normal">{field.label}</span>
            )}
            {displayValue}
          </div>
        );
      })}
    </div>
  );
};

export default StackedCellRenderer;
